import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { LoadingButton } from '../../components/shared';
import { ProjectOverdueBadge } from '../../components/shared/ProjectOverdueBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'PLANNING', label: 'Planejamento' },
  { value: 'IN_PROGRESS', label: 'Em andamento' },
  { value: 'COMPLETED', label: 'Concluído' },
  { value: 'ON_HOLD', label: 'Em espera' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

function getStatusLabel(status: string): string {
  switch (status) {
    case 'PLANNING':
      return 'PLANEJAMENTO';
    case 'IN_PROGRESS':
      return 'EM ANDAMENTO';
    case 'COMPLETED':
      return 'CONCLUÍDO';
    case 'ON_HOLD':
      return 'EM ESPERA';
    case 'CANCELLED':
      return 'CANCELADO';
    default:
      return status;
  }
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'PLANNING':
      return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200';
    case 'IN_PROGRESS':
      return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
    case 'COMPLETED':
      return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
    case 'ON_HOLD':
      return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
    case 'CANCELLED':
      return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';
    default:
      return 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200';
  }
}

export default function Projects() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { projects, loading, error, loadMore, hasMore } = useProjects({
    status: statusFilter || undefined,
  });

  // Show skeleton loading for initial load
  const showSkeleton = loading && projects.length === 0;

  if (error) {
    return (
      <div className="min-h-screen bg-primary-50 dark:bg-primary-950 flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-light tracking-tight text-primary-950 dark:text-white mb-4">
            Erro ao carregar projetos
          </h1>
          <p className="text-primary-600 dark:text-primary-400">
            {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950">
      <div className="container-elegant py-24">
        <div className="flex justify-between items-center mb-12">
          <h1 className="text-4xl font-light tracking-tight text-primary-950 dark:text-white">
            PROJETOS
          </h1>
          <Button asChild>
            <Link to="/projects/new">NOVO PROJETO</Link>
          </Button>
        </div>

        {/* Status Filter Pills */}
        <div className="mb-8 flex flex-wrap gap-3">
          {STATUS_OPTIONS.map((option) => {
            const isActive = statusFilter === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`px-5 py-2 text-xs font-light tracking-widest uppercase border transition-colors duration-300 rounded-none ${
                  isActive
                    ? 'bg-primary-950 dark:bg-white text-white dark:text-primary-950 border-primary-950 dark:border-white'
                    : 'bg-white dark:bg-primary-900 text-primary-600 dark:text-primary-400 border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* Projects Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Show skeleton cards during initial loading */}
          {showSkeleton && Array.from({ length: 6 }).map((_, index) => (
            <Card key={`skeleton-${index}`}>
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex justify-between items-center">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Show actual projects when not skeleton loading */}
          {!showSkeleton && projects.map((project: any) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="bg-white dark:bg-primary-900 border border-primary-200 dark:border-primary-800 p-6 hover:border-primary-400 dark:hover:border-primary-600 transition-colors duration-300 group"
            >
              <h3 className="text-xl font-light tracking-tight text-primary-950 dark:text-white mb-2 group-hover:text-primary-800 dark:group-hover:text-primary-200 transition-colors">
                {project.name}
              </h3>
              {project.address && (
                <p className="text-primary-600 dark:text-primary-400 mb-4">
                  {project.address}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-3 py-1 uppercase tracking-widest text-xs font-light ${getStatusClass(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                  <ProjectOverdueBadge
                    hasFinancialAccess={project.hasFinancialAccess}
                    hasOverdueInstallments={project.hasOverdueInstallments}
                  />
                </div>
                <span className="text-primary-500 dark:text-primary-500">
                  {project.members?.length || 0} membros
                </span>
              </div>
            </Link>
          ))}

          {/* Show "no projects" message when not loading and no projects */}
          {!showSkeleton && projects.length === 0 && (
            <div className="col-span-full text-center py-16">
              <p className="text-xl text-primary-600 dark:text-primary-400 mb-8">
                Nenhum projeto encontrado
              </p>
              <Button asChild>
                <Link to="/projects/new">CRIAR PRIMEIRO PROJETO</Link>
              </Button>
            </div>
          )}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="text-center mt-12">
            <LoadingButton
              onClick={loadMore}
              loading={loading && projects.length > 0}
              variant="outline"
            >
              CARREGAR MAIS
            </LoadingButton>

            {/* Show loading animation below button when loading more */}
            {loading && projects.length > 0 && (
              <div className="mt-4 flex justify-center">
                <Skeleton className="h-6 w-6 rounded-full" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}